import React from 'react';
import clsx from 'clsx';

export interface TaleemXMarkProps extends React.SVGProps<SVGSVGElement> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  tone?: 'brand' | 'inherit' | 'dark' | 'monochrome';
  tight?: boolean;
  className?: string;
}

const BARS_PATH =
  'M4400 5948 c-49 -25 -73 -69 -73 -135 0 -59 16 -90 67 -127 27 -21 38 -21 719 -24 l692 -2 42 27 c42 27 57 51 67 110 11 66 -40 148 -98 158 -17 3 -335 6 -706 7 -625 3 -678 2 -710 -14z M3865 5910 c-3 -5 -17 -10 -30 -10 -44 0 -90 -31 -130 -86 -49 -67 -60 -116 -45 -190 14 -66 50 -112 238 -298 112 -111 132 -127 149 -118 10 6 61 55 113 109 99 103 120 141 120 218 0 81 -20 115 -140 236 -102 102 -121 117 -171 132 -64 19 -95 21 -104 7z M6242 5892 c-72 -37 -644 -605 -672 -668 -27 -59 -26 -144 2 -203 22 -45 256 -291 277 -291 6 0 12 10 13 23 2 31 3 38 16 70 7 16 12 33 12 38 0 5 5 9 10 9 6 0 13 8 17 18 3 9 148 159 323 332 174 173 325 331 335 350 22 43 22 168 -2 217 -23 49 -96 109 -148 122 -72 18 -125 13 -183 -17z M4397 5514 c-4 -4 -7 -14 -7 -22 0 -8 -10 -42 -22 -76 -21 -58 -39 -78 -339 -378 -185 -185 -329 -337 -346 -365 -23 -40 -28 -59 -27 -113 1 -76 16 -112 73 -165 80 -76 182 -95 276 -50 48 23 665 631 665 655 0 9 3 20 8 24 13 13 28 100 21 121 -4 11 -13 40 -20 64 -10 35 -40 71 -144 177 -72 74 -134 131 -138 128z M6161 4994 c-25 -25 -62 -59 -83 -77 -21 -17 -54 -57 -73 -88 l-35 -58 9 -86 c12 -100 17 -109 133 -221 134 -129 193 -156 292 -130 64 17 112 48 151 101 39 50 50 173 23 230 -20 40 -344 375 -363 375 -6 0 -30 -20 -54 -46z';

const DOT_PATH =
  'M5027 5372 c-21 -9 -35 -21 -31 -25 5 -5 2 -5 -7 -2 -17 6 -62 -32 -53 -46 3 -5 0 -8 -8 -7 -7 2 -12 -3 -10 -9 1 -7 -4 -12 -10 -10 -7 1 -12 -6 -12 -15 1 -10 -2 -16 -6 -13 -12 7 -29 -41 -36 -99 -7 -60 19 -161 38 -150 6 4 8 2 4 -4 -8 -14 43 -74 56 -65 7 3 8 1 4 -5 -9 -14 39 -46 55 -36 7 4 9 3 6 -3 -4 -6 12 -16 38 -23 52 -15 132 -7 179 17 39 20 77 54 71 64 -3 4 0 11 6 15 8 5 9 3 4 -7 -8 -12 -6 -12 8 -1 9 8 26 34 38 60 33 67 32 161 -1 227 -14 28 -30 49 -37 48 -6 -1 -17 5 -25 15 -12 15 -11 16 5 3 10 -8 17 -9 17 -3 0 17 -58 54 -71 46 -7 -4 -9 -3 -6 3 21 34 -154 54 -216 25z m170 -498 c-3 -3 -12 -4 -19 -1 -8 3 -5 6 6 6 11 1 17 -2 13 -5z';

const SIZE_CLASSES: Record<string, string> = {
  xs: 'h-4 w-4',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-12 w-12',
};

/**
 * TaleemXMark - Traced icon logo mark
 * Renders the pure vector logo mark either in standard optically balanced icon viewBox
 * or tight crop mode for inline wordmark letter usage.
 */
export const TaleemXMark: React.FC<TaleemXMarkProps> = ({
  size = 'md',
  tone = 'brand',
  tight = false,
  className,
  style,
  ...props
}) => {
  const sizeClass = typeof size === 'string' ? SIZE_CLASSES[size] || 'h-8 w-8' : undefined;
  const customStyle: React.CSSProperties = {
    ...(typeof size === 'number' ? { height: size, width: size } : {}),
    ...style,
  };

  // Match traced mark: charcoal bars + lighter sky center (#60A5FA)
  let barsColor = '#2A2A2A';
  let dotColor = '#60A5FA';

  if (tone === 'inherit') {
    barsColor = 'currentColor';
    dotColor = '#60A5FA';
  } else if (tone === 'dark') {
    barsColor = '#2A2A2A';
    dotColor = '#60A5FA';
  } else if (tone === 'monochrome') {
    barsColor = 'currentColor';
    dotColor = 'currentColor';
  }

  // Tight viewBox: exact bounding box of geometry in 1024 space (x: 360..665, y: 424..596)
  // Icon viewBox: optically balanced crop with appropriate margins for avatar slots
  const viewBox = tight ? '360 424 305 172' : '340 395 345 230';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      className={clsx('shrink-0 transition-colors', sizeClass, className)}
      style={customStyle}
      role="img"
      aria-label={props['aria-label'] || 'TaleemX mark'}
      {...props}
    >
      <g transform="translate(0,1024) scale(0.1,-0.1)" fill={barsColor} stroke="none">
        <path d={BARS_PATH} />
      </g>
      <g transform="translate(0,1024) scale(0.1,-0.1)" fill={dotColor} stroke="none">
        <path d={DOT_PATH} />
      </g>
    </svg>
  );
};

export default TaleemXMark;
